import { useQuery, UseQueryOptions } from 'react-query'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { nfdGetNFDsForAddresses, NfdGetNFDsForAddressesParams } from 'src/api/api-client'
import type { NfdRecordCollection } from 'types/api'
import * as Sentry from '@sentry/nextjs'
import { chunkArray } from 'helpers/utilities'

export const getByAddresses = async (
  params: NfdGetNFDsForAddressesParams,
  ssr?: boolean,
  options?: AxiosRequestConfig
) => {
  const chunked = chunkArray(params.address)

  const responses = await Promise.all(
    chunked.map((chunk) => {
      return nfdGetNFDsForAddresses(
        {
          ...params,
          address: chunk
        },
        {
          ...(ssr && { headers: { origin: process.env.NEXT_PUBLIC_BASE_URL as string } }),
          ...options
        }
      )
    })
  )

  const results = responses.map((res) => (Array.isArray(res.data) ? res.data : [])).flat()

  return results
}

type UseAddressesArgs = {
  params: NfdGetNFDsForAddressesParams
  options?: UseQueryOptions<NfdRecordCollection, AxiosError>
}

export default function useAddresses({ params, options }: UseAddressesArgs) {
  const result = useQuery<NfdRecordCollection, AxiosError>(
    ['addresses', { ...params }],
    () =>
      getByAddresses(params, false, {
        validateStatus: (status: number) => (status >= 200 && status < 300) || status === 404
      }),
    {
      ...options
    }
  )

  if (result.error) {
    Sentry.captureException(result.error)
  }

  return result
}
